import { documentPreviewIcon, closeIcon } from "assets/images";
import NoRecords from "components/NoRecords";
import {
  TableCell,
  Box,
  SelectChangeEvent,
  Typography,
  Grid,
  Card,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  IconButton,
  InputAdornment,
  Fade,
  Tooltip,
} from "@mui/material";
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_NO,
  CLEAR_TOOLTIP_TITLE,
  STUDENT_MODULE_NAME,
} from "utils/constants/constant";
import { AppPages } from "utils/enums/app-pages";
import { Order, OrderByOptions } from "utils/enums/sorting";
import {
  hideLoaderForBatchApiCall,
  showLoaderForBatchApiCall,
  getPagePayloadValues,
  getPagePermissions,
  allowSearch,
  showLoader,
  onTextFieldKeyPress,
} from "utils/helper";
import {
  IDocumentContainerState,
  IEnhancedTableProps,
  IDocument,
  IDocumentListMeta,
  IAddEditDocument,
  IDocumentPagePayloadValues,
  IDocumentPreview,
} from "utils/interfaces/document";
import projectTheme from "app.theme";
import React, { useState, useEffect, KeyboardEvent } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { IRoleModulePermission } from "store/role/types";
import { DocumentListSuccessPayload } from "store/document/types";
import CustomPagination from "components/customPagination";
import { IStudentDocumentContainerDispatch } from "utils/interfaces/student-dashboard";
import { getUserSelector } from "store/auth/selector";
import { AuthState } from "store/rootReducer";
import { AuthState as AuthType } from "store/auth/types";

const EnhancedTableHead = (props: IEnhancedTableProps) => {
  const { order, orderBy, onRequestSort, fieldName, keyName, sxStyle } = props;
  const createSortHandler =
    (newOrderBy: keyof IDocument) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy);
    };

  return (
    <TableCell
      key={keyName}
      align="left"
      onClick={createSortHandler(keyName)}
      sortDirection={orderBy === keyName ? order : false}
      sx={sxStyle}
      className="term-sorting"
    >
      {fieldName}
      <Box component="span" className="sorting-icon" />
    </TableCell>
  );
};

export type StudentDocumentProps = IDocumentContainerState &
  IStudentDocumentContainerDispatch;

const StudentDocuments: React.FC<StudentDocumentProps> = (props) => {
  const [documentList, setDocumentList] = useState<IDocument[]>();
  const [documentListMeta, setDocumentListMeta] = useState<IDocumentListMeta>();
  const [page, setPage] = useState(DEFAULT_PAGE_SIZE);
  const [pageNo, setPageNo] = useState<number>(DEFAULT_PAGE_NO);
  const [order, setOrder] = useState<Order>(OrderByOptions.Asc);
  const [orderBy, setOrderBy] = useState<keyof IDocument>("name");
  const [searchText, setSearchText] = useState<string>("");
  const [pagePermission, setPagePermission] = useState<IRoleModulePermission>();
  const [filteredData, setFilteredData] = useState<IAddEditDocument>();
  const [appliedFilter, setAppliedFilter] = useState<boolean>(false);

  const history = useHistory();
  const user = useSelector<AuthState, AuthType["user"]>(getUserSelector);

  const handleChange = (event: SelectChangeEvent) => {
    setPage(event.target.value as string);
    setPageNo(1);
  };

  const onPageChange = (e: React.ChangeEvent<unknown>, value: number) => {
    setPageNo(value);
  };

  const handleRequestSort = React.useCallback(
    (event: React.MouseEvent<unknown>, newOrderBy: keyof IDocument) => {
      const isAsc = orderBy === newOrderBy && order === OrderByOptions.Asc;
      const toggledOrder = isAsc ? OrderByOptions.Desc : OrderByOptions.Asc;
      setOrder(toggledOrder);
      setOrderBy(newOrderBy);
    },
    [order, orderBy]
  );

  const onDocumentListSuccess = (response: DocumentListSuccessPayload) => {
    setDocumentList(response.documentList);
    setDocumentListMeta(response.documentListPagination);
    hideLoaderForBatchApiCall();
  };

  const getDocumentList = async (
    filterData?: IAddEditDocument,
    filtered?: boolean,
    pageNum?: number
  ) => {
    const { documentListRequest } = props;
    if (documentListRequest) {
      showLoaderForBatchApiCall();

      const values: IDocumentPagePayloadValues = getPagePayloadValues(
        orderBy,
        order,
        pageNo,
        page
      );
      values.searchName = filtered ? "" : searchText.trim();
      values.type = "StudentDocument";
      if (filterData) {
        setFilteredData(filterData);
        setAppliedFilter(true);

        if (pageNum) {
          values.pageNo = pageNum;
          setPageNo(pageNum);
        }
      } else {
        setFilteredData(undefined);
      }
      const payload = {
        values,
        callback: onDocumentListSuccess,
      };
      documentListRequest(payload);
    }
  };

  useEffect(() => {
    const permission = getPagePermissions(
      user?.permissions,
      AppPages.StudentDashboard,
      STUDENT_MODULE_NAME
    );
    setPagePermission(permission);
    if (permission?.view) {
      if (filteredData && !appliedFilter) {
        getDocumentList(filteredData, true, 1);
      } else if (filteredData) {
        getDocumentList(filteredData);
      } else {
        getDocumentList();
      }
    } else {
      history.goBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageNo, order, orderBy]);

  const handleDocumentSearch = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchText(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchText("");
    getDocumentList(filteredData, true, 1);
  };

  const handleDocumentSearchKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (allowSearch(e)) {
      setPageNo(1);
      getDocumentList(filteredData);
    }
  };

  const onDocumentPreviewSuccess = (response: IDocumentPreview) => {
    if (response && response.documentDetails && response.documentDetails.path) {
      const url = response.documentDetails.path;
      window.open(url, "_blank");
    }
  };

  const handleDocumentPreview = (id: number) => {
    const { documentByIdRequest } = props;
    if (documentByIdRequest) {
      showLoader();
      const payload = {
        values: {
          id: Number(id),
        },
        callback: onDocumentPreviewSuccess,
      };
      documentByIdRequest(payload);
    }
  };

  return (
    <>
      <Box className="content-header">
        <Typography variant="h2" className="heading">
          Documents
        </Typography>
      </Box>
      <Grid
        container
        spacing={{ xs: "16px", lg: "20px", xl: "24px" }}
        className="content-container"
      >
        <Grid item xs={12}>
          <Card>
            <Box className="table-card-header">
              <Box
                sx={{ justifyContent: "space-between", width: "100%" }}
                className="search-filter-inputs"
              >
                <TextField
                  id="search-box"
                  variant="outlined"
                  className="search-input"
                  placeholder="Search"
                  value={searchText}
                  onChange={handleDocumentSearch}
                  onKeyDown={handleDocumentSearchKeyDown}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        sx={{ position: "absolute", right: "0" }}
                        position="end"
                      >
                        {searchText && (
                          <Tooltip
                            TransitionComponent={Fade}
                            TransitionProps={{ timeout: 600 }}
                            title={CLEAR_TOOLTIP_TITLE}
                            arrow
                          >
                            <IconButton edge="end" onClick={handleClearSearch}>
                              <img src={closeIcon} alt="close" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  onKeyPress={(event) =>
                    onTextFieldKeyPress(event, searchText?.length)
                  }
                />
              </Box>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <EnhancedTableHead
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={(e) => handleRequestSort(e, "name")}
                      fieldName="Document Name"
                      keyName="name"
                      sxStyle={{
                        maxWidth: "281px",
                        width: "281px",
                        minWidth: "281px",
                      }}
                    />
                    <TableCell
                      align="left"
                      sx={{
                        width: "245px",
                        minWidth: "245px",
                        [projectTheme.breakpoints.down("sm")]: {
                          width: "245px",
                        },
                      }}
                    >
                      Document Link
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        width: "355px",
                        [projectTheme.breakpoints.down("sm")]: {
                          width: "355px",
                        },
                      }}
                    >
                      Description
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: "100px",
                        [projectTheme.breakpoints.down("sm")]: {
                          width: "112px",
                        },
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documentList?.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell
                        sx={{ wordWrap: "break-word" }}
                        component="th"
                        scope="row"
                      >
                        {row.name}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className="email">
                          {row.link}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className="email">
                          {row.description || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <div className="table-actions">
                          <Tooltip
                            TransitionComponent={Fade}
                            TransitionProps={{ timeout: 600 }}
                            title="Preview"
                            arrow
                          >
                            <IconButton
                              onClick={() => handleDocumentPreview(row.id)}
                              disabled={!pagePermission?.view}
                              className={
                                !pagePermission?.edit ? "disabled-icon" : ""
                              }
                            >
                              <img src={documentPreviewIcon} alt="edit" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {documentList && documentList.length > 0 ? (
              <CustomPagination
                handleChange={handleChange}
                page={page}
                pageNo={pageNo}
                onPageChange={onPageChange}
                listMeta={documentListMeta}
              />
            ) : (
              <NoRecords />
            )}
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default StudentDocuments;
